import { LitElement, html, css } from 'lit';
import { accessibility, formStyles, grid } from '../../utilities/styles';
import { cardLinks } from '../../utilities/accessibility';
import { EventManager } from '../../utilities/events';
import { cache } from 'lit/directives/cache.js';
import { ContextConsumer } from '@lit/context';
import { organisationContext } from '../../utilities/context';
import { arrowBack } from '../../utilities/icons';
import { AllInSessionStorage } from '../Utilities/AllInSessionStorage';
import { AllInOrganisationConsumer } from '../Utilities/AllInOrganisationConsumer';
import { handle403s } from '../../utilities/AllInErrorHandling';
export class AllInEventsManager extends AllInSessionStorage(AllInOrganisationConsumer(LitElement)) {
    static properties = {
        currentOrg: { type: String, reflect: true },
        _events: { state: true, type: Array },
        _currentPage: { state: true, type: Number },
    };
    constructor() {
        super();
        this._currentPage = 1;
    }
    async connectedCallback() {
        super.connectedCallback();
        this.eventManager.listen(`allin-organisation-events-page`, (e) => { this.currentPage = e.detail.page; });
        this.organisationData = this._organisationData?.value;
        if (!this.currentOrg) {
            return;
        }
        if (!this.organisation) {
            this.organisation = this._getOrganisationById(this.currentOrg);
        }
        if (this.organisation && !this.organisation.locations) {
            this._maybeGetLocationsFromSessionStorage();
            if (!this.organisation?.locations || this.organisation.locations.length == 0) {
                await this._fetchLocations();
            }
            else {
                this._fetchLocations();
            }
        }
        if (this.panel) {
            this._showPanel(this.panel, this.view);
        }
        if (this.organisation && !this.organisation.events) {
            this.initialFetch = true;
            await this._fetchEvents(this._currentPage);
            this.initialFetch = false;
        }
        this.renderRoot.querySelector('button,a')?.focus();
        cardLinks(this.shadowRoot);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.eventManager.disconnect();
    }
    async updated(changedProperties) {
        if (changedProperties.has('currentOrg')) {
            this.organisation = this._getOrganisationById(this.currentOrg);
            if (this.organisation && !this.organisation.locations) {
                await this._fetchLocations();
            }
            if (this.organisation && !this.organisation.events) {
                await this._fetchEvents(this._currentPage);
            }
        }
        if (changedProperties.has('panel') || changedProperties.has('view')) {
            this.renderRoot.querySelector('button,a')?.focus();
            if (this.panel == '' && this.prevPanel !== '') {
                this._hidePanel();
            }
            else if (this.panel && this.panel != this.prevPanel) {
                this._showPanel(this.panel, this.view);
            }
        }
        if (changedProperties.has('currentPage')) {
            if (this._currentPage && !this.organisation.events?.[`page_${this._currentPage}`]) {
                this._fetchEvents(this._currentPage);
            }
        }
        cardLinks(this.shadowRoot);
    }
    render() {
        let main = '';
        let button = html `<button class="back" @click=${this._hidePanel}><span aria-hidden="true">${arrowBack}</span>Return to all events</button>`;
        if (this.panel == 'add') {
            main = html `\n                ${button}\n                <allin-organisation-event-form currentOrg=${this.currentOrg} event="" view="add"></allin-organisation-event-form>\n            `;
        }
        else if (this.panel) {
            main = html `\n                ${button}\n\n                <allin-organisation-event-form currentOrg=${this.currentOrg} event=${this.panel} view=${this.view}></allin-organisation-event-form>\n            `;
        }
        else {
            main = html `\n                <allin-organisation-events name=${this.name} page=${this._currentPage}></allin-organisation-events>\n            `;
        }
        return html `${main}`;
    }
    _showPanel(panel, view) {
        this.panel = panel;
        let name = '';
        if (this.panel == 'add') {
            name = 'New Event';
        }
        else {
            let event = this._getEventById(this.panel);
            if (event) {
                name = event.name;
            }
            else {
                name = 'Event';
            }
        }
        if (view) {
            panel += `/${view}`;
        }
        if (!this.organisation) {
            this.organisation = this._getOrganisationById(this.currentOrg);
        }
        let crumb = {
            organisation: { url: `/organisation-dashboard/${this.currentOrg}/`, title: `${this.organisation?.name}` || `` },
            dashboard: { url: `/organisation-dashboard/${this.currentOrg}/events/`, title: `Manage Events` },
            panel: { url: `/organisation-dashboard/${this.currentOrg}/events/${this.panel}/`, title: name }
        };
        this.eventManager.emit(`allin-breadcrumb-${this.name}`, crumb);
        this.eventManager.addHistory('/organisation-dashboard/' + this.currentOrg + '/events/' + panel, { panel: this.panel });
    }
    _hidePanel() {
        this.panel = '';
        this.view = '';
        if (!this.organisation) {
            this.organisation = this._getOrganisationById(this.currentOrg);
        }
        let crumb = {
            organisation: { url: `/organisation-dashboard/${this.currentOrg}/`, title: `${this.organisation?.name}` || `` },
            dashboard: { url: `/organisation-dashboard/${this.currentOrg}/events/`, title: `Manage Events` }
        };
        this.eventManager.emit(`allin-breadcrumb-${this.name}`, crumb);
        this.eventManager.addHistory('/organisation-dashboard/' + this.currentOrg + '/events', { panel: '' });
        this._updateContext({ panel: '', view: '' });
        this.partial = false;
    }
    _beforeContextUpdate(value) {
        this.prevPanel = this.panel;
    }
    _getLocationById(id) {
        let lId = this.organisation?.locations?.find((loc) => loc.id == id);
        return lId;
    }
    _getOrganisationById(id) {
        return this.organisations.find((org) => org.org_id == id);
    }
    _getEventById(id) {
        return this.organisation?.events?.[id];
    }
    async _fetchLocations() {
        let url = `${this.restUrl}?org_id=${this.currentOrg}&action=${this.action}-locations&nonce=${this.nonce}`;
        try {
            let response = await fetch(url, {
                headers: {
                    'AllIn-Nonce': this.nonce,
                }
            });
            if (response.status === 403) {
                handle403s();
                return;
            }
            let data = await response.json();
            let locations = data.locations;
            for (let i = 0; i < locations.length; i++) {
                let isLocked = await checkFormLock(`location__${locations[i].id}__${this.currentOrg}`, this.currentUser.id, this.restUrl, this.nonce);
                locations[i].isLocked = isLocked;
            }
            this.organisation.locations = locations;
            this.eventManager.emit(`allin-organisation-${this.name}-update`, { organisation: JSON.stringify(this.organisation) });
        }
        catch (error) {
            console.error('Error:', error);
        }
        ;
    }
    async _fetchEvents(page) {
        if (this.initialFetch) {
            return;
        }
        let url = `${this.restUrl}?org_id=${this.currentOrg}&action=${this.action}-events&nonce=${this.nonce}&page=${page}`;
        try {
            let response = await fetch(url, {
                headers: {
                    'AllIn-Nonce': this.nonce,
                }
            });
            if (response.status === 403) {
                handle403s();
                return;
            }
            let data = await response.json();
            if (!this.organisation.hasOwnProperty('events')) {
                this.organisation.events = {};
            }
            this.organisation.events[`page_${page}`] = data.events.data;
            data.events.data.forEach((event) => {
                this.organisation.events[event.id] = event;
            });
            this.organisation.events.total = data.events.total;
            this.eventManager.emit(`allin-organisation-${this.name}-update`, { organisation: JSON.stringify(this.organisation) });
        }
        catch (error) {
            console.error('Error:', error);
        }
    }
    static styles = [
        accessibility,
        formStyles,
        grid,
        css `
        allin-greeting{
            --allin-font-size: 2rem;
            --allin-font-weight: 700;
        }
        .bg{
            padding:var(--allin-padding,2rem);
            background-color:var(--allin-bg-color,#ccc);
            display:grid;
            gap:1rem;
        }
        .small{
            font-size:0.875rem;
        }

        .grid{
            gap:1.5rem;
        }
        
        .card a{
            display:block;
            padding:1rem;
            border-radius:var(--allin-border-radius,0.5rem);
            background-color:var(--allin-highlight-color,#472961);
            color:#fff;
            text-align:center;
            font-weight:700;
            text-decoration:none;
            transition:background-color var(--transition) ease;
            height:fit-content;
            align-self:end;
        }

        .card:hover a{
            background-color:var(--allin-highlight-color-lighter,#5e3e7d);
        }


        h3,p{
            margin:0;
        }

        fieldset{
            border:0;
            padding:0;
            margin:0;
        }

        legend{
            font-size:1.25rem;
            font-weight:700;
            margin-bottom:1rem;
            color:var(--allin-highlight-color,#472961);
        }
        legend:has(+p){
            margin-bottom:0.5rem;
        }

        .button-group{
            display:flex;
            justify-content:space-between;
            margin-top:2rem;
        }

        .border-top{
            border-top:1px solid var(--allin-highlight-color,#472961);
            padding-top:2rem;
        }
        .button-group button, .return{
            background-color:transparent;
            color:var(--allin-highlight-color,#472961);
            border:0;
            display:flex;
            gap:0.5rem;
            align-items:center;
            text-decoration:underline;
            cursor:pointer;
            transition:color var(--transition) ease;
            font-weight:700;
            font-size:1rem;

        }
        .return{
            padding:0;
            margin-bottom:2rem;
        }

        .button-group button svg, .return svg{
            transition:transform var(--transition) ease;
        }
        .button-group button:hover, .return:hover{
            text-decoration-thickness:2px;
        }
        
        .button-group button:hover svg, .return:hover svg{
            transform:rotate(15deg);
        }
        
        table{
            width:100%;
            border-collapse:collapse;
        }

        td{
            padding-bottom:2rem;
            word-break:break-all;
        }

        td:first-child{
            width:40%;
            padding-right:2rem;
            vertical-align:top;
        }

        td:last-child{
            width:60%;
            font-weight:600;
            vertical-align:top;
        }
        
        td svg{
            height:0.5rem;
        }
        ul{
            list-style:none;
            padding:0;
            margin:0;
        }

        button.back{
            color:var(--allin-highlight-color,#472961);
            background-color:transparent;
            border:0;
            padding:0;
            font-weight:700;
            display:flex;
            align-items:center;
            gap:0.5rem;
        }
        button.back span{
            display:flex;
            align-items:center;
            justify-content:center;
            width:1rem;
        }
        button.back svg{
            transform:rotate(0);
            transition:transform var(--transition) ease;
            width:1rem;
        }
        button.back:hover{
            text-decoration:underline;
        }

        button.back:hover svg{
            transform:rotate(15deg);
        }

        @media (prefers-reduced-motion: reduce) {
            button.back svg{
                transform:none !important;
            }
        }

        `
    ];
}
